import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * A failed login always comes back as `?authError=...` (the Worker's `auth.routes.ts` `callback` handler's
 * `fail()` redirects every failure to `${appUrl}/?authError=...` — never to the originally requested URL).
 * Returns where to go instead of showing that error inline, or `null` when there is none.
 */
function authErrorRedirect(router: Router, url: string): UrlTree | null {
  const authError = router.parseUrl(url).queryParams['authError'] as string | undefined;
  return authError ? router.createUrlTree(['/access-pending'], { queryParams: { authError } }) : null;
}

/**
 * Runs the `authErrorRedirect()` check above even outside the guarded app shell. Needed since Phase 3: "/"
 * is now the public landing page and no longer redirects into `/discover`, so `authGuard` below (which only
 * runs for the guarded routes) would never see a failed login's `?authError=...` on "/" and silently drop
 * it. Applied only to "/" (see app.routes.ts) — the Worker never targets "/welcome" or any other route with
 * `authError`, so nothing else needs it. Inactive in prototype mode, like every guard in this file.
 */
export const authErrorGuard: CanActivateFn = (_route, state) => {
  if (!inject(AuthService).enabled) return true;
  return authErrorRedirect(inject(Router), state.url) ?? true;
};

/** Resolves to true, or to where the user should go instead. Starts the login for signed out users. */
async function ensureSignedIn(url: string): Promise<true | false | UrlTree> {
  const auth = inject(AuthService);
  const router = inject(Router);

  const redirect = authErrorRedirect(router, url);
  if (redirect) return redirect;

  await auth.load();
  switch (auth.status()) {
    case 'authenticated':
      return true;
    case 'unauthenticated':
      auth.login(url);
      return false;
    default:
      return router.createUrlTree(['/access-pending']);
  }
}

/**
 * UX only: sends signed out users to the login and unprovisioned users to an explanation page.
 * It protects nothing by itself. Every API call is authorized by the Worker.
 * Inactive in prototype mode (environment.requireAuth is false).
 */
export const authGuard: CanActivateFn = async (_route, state) => {
  if (!inject(AuthService).enabled) return true;
  return ensureSignedIn(state.url);
};

/** The operator portal needs the platform role. In prototype mode the mock role switcher decides. */
export const superAdminGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  if (!auth.enabled) return true;
  const router = inject(Router);
  const result = await ensureSignedIn(state.url);
  if (result !== true) return result;
  return auth.me()?.role === 'SUPER_ADMIN' ? true : router.createUrlTree(['/discover']);
};
